import type { AuthoringDoc } from '@personalizehq/editor';
import type { Theme } from '@personalizehq/schemas/ir';
import type { ApiResponse } from '@personalizehq/types';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { api } from './api';

// One data layer over the existing `api` client. Pages call these hooks; no page writes its own
// fetch. `api` never throws — it returns a discriminated ApiResponse — but react-query needs a
// rejected promise to populate `error`, so `unwrap` is the single seam that converts one to the
// other. Doing that per-hook is how a swallowed error eventually reaches a user as a silent no-op.
async function unwrap<T>(result: Promise<ApiResponse<T>>): Promise<T> {
  const response = await result;
  if (!response.success) throw new Error(response.error.message);
  return response.data;
}

// ---------------------------------------------------------------------------- context

export interface Environment {
  id: string;
  name: string;
  sdk_enabled: boolean;
}
export interface App {
  id: string;
  name: string;
  environments: Environment[];
}
export interface Workspace {
  id: string;
  name: string;
  apps: App[];
}
export interface Org {
  id: string;
  name: string;
  role: 'admin' | 'editor' | 'viewer';
  workspaces: Workspace[];
}

export function useContextTree() {
  return useQuery({
    queryKey: ['context'],
    queryFn: () => unwrap(api.get<{ orgs: Org[] }>('/api/context')),
    select: (data) => data.orgs,
  });
}

// ---------------------------------------------------------------------------- generic CRUD

/** Every list is scoped to an app. `enabled` guards the render before the context store has one. */
function useAppScopedList<T>(resource: string, key: string, appId: string | null) {
  return useQuery({
    queryKey: [resource, appId],
    queryFn: () => unwrap(api.get<Record<string, T[]>>(`/api/${resource}?app_id=${appId}`)),
    select: (data) => data[key] ?? [],
    enabled: Boolean(appId),
  });
}

/** Invalidate on success, always. A create that leaves a stale list on screen reads as a failure. */
function useInvalidating<TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
  invalidate: QueryKey[]
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      for (const key of invalidate) void client.invalidateQueries({ queryKey: key });
    },
  });
}

// ---------------------------------------------------------------------------- campaigns

export interface Campaign {
  id: string;
  app_id: string;
  environment_id: string;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'archived';
  priority: 'active' | 'passive' | 'time_sensitive';
  schedule: { start: string; end?: string } | null;
  trigger_config: { type: 'always' } | { type: 'on_event'; event: string } | { type: 'on_screen'; screen: string } | null;
  frequency_cap: { session_limit: number; cross_session_limit: number; window_days: number } | null;
  segment_id: string | null;
  experience_id: string | null;
  /** WHERE it renders: a floating overlay, or a slot in the host's own screen. The column has
   *  existed since migration 0007 and the API has always accepted it — the dashboard just had no
   *  field for it, which is how `inline` (a v1 requirement) stayed unauthorable. */
  placement: { kind: 'overlay' } | { kind: 'inline'; slot: string };
  published_bundle_version: number | null;
  updated_at: string;
}

export function useCampaigns(appId: string | null) {
  return useAppScopedList<Campaign>('campaigns', 'campaigns', appId);
}

export function useCampaign(id: string) {
  return useQuery({
    queryKey: ['campaign', id],
    queryFn: () => unwrap(api.get<Campaign>(`/api/campaigns/${id}`)),
  });
}

export function useCreateCampaign(appId: string | null) {
  return useInvalidating(
    (body: Partial<Campaign> & { environment_id: string; name: string }) =>
      unwrap(api.post<Campaign>('/api/campaigns', { ...body, app_id: appId })),
    [['campaigns', appId]]
  );
}

export function useUpdateCampaign(id: string, appId: string | null) {
  return useInvalidating(
    (body: Partial<Campaign>) => unwrap(api.patch<Campaign>(`/api/campaigns/${id}`, body)),
    [['campaign', id], ['campaigns', appId]]
  );
}

export function useCampaignVersions(id: string) {
  return useQuery({
    queryKey: ['campaign-versions', id],
    queryFn: () => unwrap(api.get<{ current: number | null; versions: number[] }>(`/api/campaigns/${id}/versions`)),
  });
}

export function usePublish(id: string, appId: string | null) {
  return useInvalidating(
    () => unwrap(api.post<{ published_bundle_version: number; campaigns: number }>(`/api/campaigns/${id}/publish`)),
    [['campaign', id], ['campaigns', appId], ['campaign-versions', id]]
  );
}

export function useRollback(id: string, appId: string | null) {
  return useInvalidating(
    (targetVersion: number) =>
      unwrap(api.post<{ published_bundle_version: number }>(`/api/campaigns/${id}/rollback`, { target_version: targetVersion })),
    [['campaign', id], ['campaigns', appId], ['campaign-versions', id]]
  );
}

// ---------------------------------------------------------------------------- segments

export interface Segment {
  id: string;
  name: string;
  definition: unknown;
}

export function useSegments(appId: string | null) {
  return useAppScopedList<Segment>('segments', 'segments', appId);
}

export function useSegment(id: string) {
  return useQuery({
    queryKey: ['segment', id],
    queryFn: () => unwrap(api.get<Segment>(`/api/segments/${id}`)),
  });
}

export function useCreateSegment(appId: string | null) {
  return useInvalidating(
    (body: { name: string; definition: unknown }) =>
      unwrap(api.post<Segment>('/api/segments', { ...body, app_id: appId })),
    [['segments', appId]]
  );
}

export function useUpdateSegment(id: string, appId: string | null) {
  return useInvalidating(
    (body: { name?: string; definition?: unknown }) => unwrap(api.patch<Segment>(`/api/segments/${id}`, body)),
    [['segment', id], ['segments', appId]]
  );
}

export function useDeleteSegment(appId: string | null) {
  return useInvalidating((id: string) => unwrap(api.delete(`/api/segments/${id}`)), [['segments', appId]]);
}

// ---------------------------------------------------------------------------- experiences

export interface Experience {
  id: string;
  name: string;
  type: 'modal' | 'fullscreen' | 'banner' | 'bottom_sheet';
  version: number;
  updated_at: string;
}

export function useExperiences(appId: string | null) {
  return useAppScopedList<Experience>('experiences', 'experiences', appId);
}

/** The list response omits `document` (a 40-experience list would ship a megabyte of JSON it throws
 *  away). The single GET carries it — this is what the builder loads. */
export function useExperience(id: string) {
  return useQuery({
    queryKey: ['experience', id],
    // An unlinked campaign passes '' — there is nothing to fetch, and `GET /api/experiences/` is a
    // 404 nobody asked for.
    enabled: id !== '',
    queryFn: () => unwrap(api.get<Experience & { document: AuthoringDoc }>(`/api/experiences/${id}`)),
    // The builder OWNS the doc once it is loaded (the session store is the truth from then on). A
    // refetch that swapped the doc out from under an open canvas would silently discard edits.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useDeleteExperience(appId: string | null) {
  return useInvalidating((id: string) => unwrap(api.delete(`/api/experiences/${id}`)), [['experiences', appId]]);
}

export function useCreateExperience(appId: string | null) {
  return useInvalidating(
    (body: { name: string; document: AuthoringDoc }) =>
      unwrap(api.post<Experience & { document: AuthoringDoc }>('/api/experiences', { ...body, app_id: appId })),
    [['experiences', appId]]
  );
}

/** The autosave endpoint. A `document` write bumps `version` and re-derives `type` server-side. */
export function useUpdateExperience(id: string, appId: string | null) {
  return useInvalidating(
    (body: { name?: string; document?: AuthoringDoc }) => unwrap(api.patch<Experience>(`/api/experiences/${id}`, body)),
    [['experiences', appId]]
  );
}

/** PREVIEW ON DEVICE (B6). Mints a 15-minute token; the dashboard renders it as a QR the phone
 *  scans. Invalidates nothing — a preview changes no server state, which is the entire point of
 *  compiling the draft in memory. */
export function usePreviewToken(id: string) {
  return useMutation({
    mutationFn: () => unwrap(api.post<{ token: string; expires_in: number }>(`/api/experiences/${id}/preview`)),
  });
}

// ---------------------------------------------------------------------------- theme

/** NULL until a theme is first written — there is no invented server-side default. The dashboard
 *  seeds one (`lib/seed-theme.ts`); the app theme is then embedded into every doc it saves. */
export function useAppTheme(appId: string | null) {
  return useQuery({
    queryKey: ['theme', appId],
    queryFn: () => unwrap(api.get<{ theme: Theme | null }>(`/api/apps/${appId}/theme`)),
    select: (data) => data.theme,
    enabled: Boolean(appId),
  });
}

export function useSetAppTheme(appId: string | null) {
  return useInvalidating(
    (theme: Theme) => unwrap(api.put<{ theme: Theme }>(`/api/apps/${appId}/theme`, theme)),
    [['theme', appId]]
  );
}

// ---------------------------------------------------------------------------- assets

export interface Asset {
  id: string;
  r2_key: string;
  url: string;
  content_type: string;
  width: number | null;
  height: number | null;
  created_at: string;
}

/** The publish-time law (p3-authoring-dashboard.md §2). The server enforces it too — this is UX. */
export const MAX_ASSET_BYTES = 500 * 1024;

export function useAssets(appId: string | null) {
  return useAppScopedList<Asset>('assets', 'assets', appId);
}

export function useUploadAsset(appId: string | null) {
  return useInvalidating(async (file: File) => {
    if (file.size > MAX_ASSET_BYTES) {
      throw new Error(`${file.name} is ${Math.round(file.size / 1024)} KB — the limit is 500 KB.`);
    }
    const query = `app_id=${appId}&filename=${encodeURIComponent(file.name)}`;
    return unwrap(api.postBytes<Asset>(`/api/assets?${query}`, await file.arrayBuffer(), file.type));
  }, [['assets', appId]]);
}

export function useDeleteAsset(appId: string | null) {
  return useInvalidating((id: string) => unwrap(api.delete(`/api/assets/${id}`)), [['assets', appId]]);
}

// ---------------------------------------------------------------------------- write keys

export interface WriteKey {
  id: string;
  environment_id: string;
  created_at: string;
  revoked_at: string | null;
}

export function useWriteKeys(environmentId: string | null) {
  return useQuery({
    queryKey: ['write-keys', environmentId],
    queryFn: () => unwrap(api.get<{ write_keys: WriteKey[] }>(`/api/write-keys?environment_id=${environmentId}`)),
    select: (data) => data.write_keys ?? [],
    enabled: Boolean(environmentId),
  });
}

/** The response carries the raw `key` exactly once — the server stores only its hash. */
export function useCreateWriteKey(environmentId: string | null) {
  return useInvalidating(
    () => unwrap(api.post<WriteKey & { key: string }>('/api/write-keys', { environment_id: environmentId })),
    [['write-keys', environmentId]]
  );
}

export function useRevokeWriteKey(environmentId: string | null) {
  return useInvalidating((id: string) => unwrap(api.delete(`/api/write-keys/${id}`)), [
    ['write-keys', environmentId],
  ]);
}

// ---------------------------------------------------------------------------- environments

export function useSetSdkEnabled() {
  return useInvalidating(
    ({ id, sdkEnabled }: { id: string; sdkEnabled: boolean }) =>
      unwrap(api.patch(`/api/environments/${id}`, { sdk_enabled: sdkEnabled })),
    [['context']]
  );
}
